import React from 'react'
import CodeMirror from '@uiw/react-codemirror'
import { Button, IconButton } from 'transfix-ui/components'
import { Box, makeStyles, Typography } from 'transfix-ui/core'
import {
  IConfigurableBuilderTest,
  IConfigurableBuilderTestResult,
  ICreateConfigurableBuilderTestInput,
  IUpdateConfigurableBuilderTestInput,
  IConfigurableBuilderTestResultStatuses,
  IRunConfigurableBuilderTestsInput,
  IStupidEdiConfigs,
  IBuilderLibraries,
  Maybe,
} from 'types/graphqlTypes'
import { ClearIcon } from 'transfix-icons'
import { useModal } from 'utils/hooks'
import { formatForCodeEditor } from '../utils/formatting'
import { ConfigurableBuilderTestOptionsModal } from './ConfigurableBuilderTestOptionsModal'
import { TestListModal } from './TestListModal'
import { TestResultTag } from './TestResultTag'

const useStyles = makeStyles(() => ({
  row: {
    width: '100%',
    direction: 'ltr',
    display: 'flex',
    alignItems: 'left',
    justifyContent: 'flex-start',
    flexWrap: 'nowrap',
  },
  rowCenter: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  editor: {
    borderRight: '1px solid',
    '& .cm-content': {
      border: '1px solid',
      borderRight: 'none',
    },
    '& .cm-lineNumbers': {
      minWidth: 33,
    },
    '& .cm-line::selection': {
      backgroundColor: '#d9d9d9 !important',
    },
  },
  column: {
    flex: 10,
  },
}))

interface ITestManagementProps {
  builderId?: string
  configurableBuilderTests: IConfigurableBuilderTest[]
  createConfigurableBuilderTest(input: ICreateConfigurableBuilderTestInput): void
  currentBuilderTest: IConfigurableBuilderTest
  ediResult: string | null
  jsonResult: string | null
  latestTestResults: IConfigurableBuilderTestResult[]
  outboundConfigId: string
  runConfigurableBuilderTests(input: IRunConfigurableBuilderTestsInput): void
  extractionTemplate: string
  builderLibrary: IBuilderLibraries
  stupidediConfig: Maybe<IStupidEdiConfigs>
  tfx12DefinitionId: Maybe<string>
  setCurrentBuilderTest(currentBuilderTest: IConfigurableBuilderTest): void
  testsAreRunning: boolean
  updateConfigurableBuilderTest(input: IUpdateConfigurableBuilderTestInput): void
}

export const TestManagement = ({
  builderId,
  builderLibrary,
  configurableBuilderTests,
  createConfigurableBuilderTest,
  currentBuilderTest,
  ediResult,
  extractionTemplate,
  jsonResult,
  latestTestResults,
  outboundConfigId,
  runConfigurableBuilderTests,
  setCurrentBuilderTest,
  stupidediConfig,
  testsAreRunning,
  tfx12DefinitionId,
  updateConfigurableBuilderTest,
}: ITestManagementProps) => {
  const { closeModal, isOpen, openModal } = useModal()
  const classes = useStyles()

  const handleSaveBuilderTest = (data: IConfigurableBuilderTest) => {
    const input: any = {
      ...currentBuilderTest,
      name: data.name,
      notes: data?.notes,
      isLive: data?.isLive,
      expectedOutput: JSON.parse(jsonResult || ''),
    }

    if (currentBuilderTest.id) {
      updateConfigurableBuilderTest(input)
    } else {
      input.ediMapperConfigurableBuilderId = builderId
      createConfigurableBuilderTest(input)
    }
  }

  const runAllTestsHandler = () => {
    runConfigurableBuilderTests({
      builderId,
      extractionTemplate,
      builderLibrary,
      stupidediConfig,
      outboundConfigId,
      tfx12DefinitionId,
    })
  }
  const formattedExpectedOutput = formatForCodeEditor(currentBuilderTest.expectedOutput)

  const latestResultForTest = (testId: string): IConfigurableBuilderTestResult | undefined => {
    return latestTestResults?.find(elem => elem.id === testId)
  }

  const latestResultForCurrentTest = latestResultForTest(currentBuilderTest.id)

  return (
    <>
      {isOpen('configurableBuilderTestOptionsModal') && (
        <ConfigurableBuilderTestOptionsModal
          onClose={closeModal}
          onCancel={closeModal}
          onSubmit={handleSaveBuilderTest}
          options={currentBuilderTest}
        />
      )}
      {isOpen('testListModal') && (
        <TestListModal
          showRunAllTestsButton={!!extractionTemplate}
          allTests={configurableBuilderTests}
          testsAreRunning={testsAreRunning}
          latestTestResults={latestTestResults}
          runAllTestsHandler={runAllTestsHandler}
          currentBuilderTest={currentBuilderTest}
          onClose={closeModal}
          onCancel={closeModal}
          setCurrentBuilderTest={setCurrentBuilderTest}
        />
      )}
      {configurableBuilderTests.length > 0 && (
        <>
          <hr />
          <Box component='div' mt={3} ml={2} className={`${classes.row}`}>
            <Typography variant='h3'>Tests:</Typography>
          </Box>
          <Box component='div' className={classes.row}>
            <Box className={`${classes.column}`} marginX={2}>
              <Button color='tertiary' variant='contained' onClick={openModal('testListModal')}>
                Manage Existing Builder Tests
              </Button>
            </Box>
          </Box>
        </>
      )}
      {currentBuilderTest.id && (
        <Box component='div' className={classes.row}>
          <Box className={`${classes.column}`} marginX={2}>
            <Typography>
              Using Test: {currentBuilderTest.name}
              <IconButton
                Icon={ClearIcon}
                color='secondary'
                onClick={() =>
                  setCurrentBuilderTest({
                    ...currentBuilderTest,
                    ...{ id: '', name: '', notes: '', expectedOutput: [] },
                  })
                }
                size='large'
              />
            </Typography>
          </Box>
        </Box>
      )}
      {currentBuilderTest.id && (
        <Box>
          <Box className={`${classes.column}`} marginX={2}>
            <Typography>
              Test Result:
              <TestResultTag status={latestResultForCurrentTest?.status} />
            </Typography>
          </Box>
        </Box>
      )}
      {jsonResult && ediResult && (
        <Box component='div' className={classes.row}>
          <Box className={`${classes.column}`} marginX={2}>
            <Button onClick={openModal('configurableBuilderTestOptionsModal')}>
              {currentBuilderTest.id ? 'Update' : 'Create'} Test
            </Button>
          </Box>
        </Box>
      )}
      <Box component='div' className={classes.row}>
        {currentBuilderTest.id && (
          <Box className={`${classes.column}`} marginX={2}>
            <Box
              marginY={1}
              display='flex'
              flexDirection='row'
              flexWrap='wrap'
              justifyContent='space-between'>
              <Typography variant='h3'>Expected Output</Typography>
            </Box>
            <CodeMirror
              height='40vh'
              maxWidth='30vw'
              className={classes.editor}
              value={formattedExpectedOutput}
              editable={false}
            />
          </Box>
        )}
        {latestResultForCurrentTest?.status === IConfigurableBuilderTestResultStatuses.FAILED && (
          <Box className={`${classes.column}`} marginX={2}>
            <Box
              marginY={1}
              display='flex'
              flexDirection='row'
              flexWrap='wrap'
              justifyContent='space-between'>
              <Typography variant='h3'>Diffs</Typography>
              <Typography variant='body1'>
                Changes required to make the actual output match the expected output.
                <ul>
                  <li>
                    <code>+</code> to add an element
                  </li>
                  <li>
                    <code>-</code> to remove an element
                  </li>
                  <li>
                    <code>~</code> to update an element in-place
                  </li>
                </ul>
              </Typography>
            </Box>
            <CodeMirror
              height='40vh'
              maxWidth='30vw'
              className={classes.editor}
              value={formatForCodeEditor(latestResultForCurrentTest?.diffs)}
              editable={false}
            />
          </Box>
        )}
      </Box>
    </>
  )
}
